import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DollarSign, Calendar, CheckCircle2, RefreshCw, GraduationCap, Briefcase, HeartHandshake, Loader2, Pencil, CalendarPlus, Search, Ban, Power, Baby } from "lucide-react";

const TYPE_META = {
  school: { label: "School", Icon: GraduationCap, pill: "bg-blue-100 text-blue-800" },
  business: { label: "Business/Office", Icon: Briefcase, pill: "bg-slate-100 text-slate-700" },
  care: { label: "Personal Care Home", Icon: HeartHandshake, pill: "bg-emerald-100 text-emerald-800" },
  daycare: { label: "Daycare / Pre-K", Icon: Baby, pill: "bg-pink-100 text-pink-800" },
};
const typeMeta = (t) => TYPE_META[t] || TYPE_META.business;
import { useWindowed, ShowMoreBar } from "@/components/ShowMore";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const TYPE_OPTS = [
  { v: "all", label: "All types" },
  { v: "school", label: "Schools" },
  { v: "business", label: "Offices" },
  { v: "care", label: "Care homes" },
  { v: "daycare", label: "Daycares" },
];
const STATUS_OPTS = [
  { v: "all", label: "All statuses" },
  { v: "active", label: "Active" },
  { v: "trial", label: "Trial" },
  { v: "expired", label: "Expired" },
  { v: "deactivated", label: "Deactivated" },
];

export default function PlatformCompanies() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [savingDefaultPrice, setSavingDefaultPrice] = useState(false);
  const [extendFor, setExtendFor] = useState(null);
  const [extendDays, setExtendDays] = useState("30");
  const [savingExtend, setSavingExtend] = useState(false);
  const [typeFor, setTypeFor] = useState(null);
  const [newType, setNewType] = useState("business");
  const [savingType, setSavingType] = useState(false);
  const [deactFor, setDeactFor] = useState(null);
  const [savingDeact, setSavingDeact] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get("/platform/stats"), api.get("/platform/companies")]);
      setStats(s.data);
      setCompanies(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveExtend = async (e) => {
    e?.preventDefault();
    const days = Number(extendDays);
    if (!days || days < 1) { toast.error("Enter at least 1 day."); return; }
    setSavingExtend(true);
    try {
      await api.post(`/platform/company/${extendFor.id}/extend`, { days });
      toast.success(`${extendFor.name} extended by ${days} days`);
      setExtendFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to extend");
    } finally {
      setSavingExtend(false);
    }
  };

  const saveType = async () => {
    if (!typeFor || savingType) return;
    if (newType === (typeFor.type || "business")) { setTypeFor(null); return; }
    setSavingType(true);
    try {
      await api.put(`/platform/company/${typeFor.id}/type`, { type: newType });
      toast.success(`${typeFor.name} is now a ${typeMeta(newType).label}`);
      setTypeFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to change type");
    } finally {
      setSavingType(false);
    }
  };

  const saveDeact = async () => {
    if (!deactFor || savingDeact) return;
    const action = deactFor.deactivated ? "reactivate" : "deactivate";
    setSavingDeact(true);
    try {
      await api.post(`/platform/company/${deactFor.id}/${action}`);
      toast.success(`${deactFor.name} ${action}d`);
      setDeactFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Failed to ${action}`);
    } finally {
      setSavingDeact(false);
    }
  };

  const companyStatus = (c) => {
    if (c.deactivated) return { label: "Deactivated", tone: "slate", ref: null, key: "deactivated" };
    const now = new Date();
    const paid = c.paid_until ? new Date(c.paid_until) : null;
    const trial = c.trial_ends_at ? new Date(c.trial_ends_at) : null;
    if (paid && paid > now) return { label: "Active", tone: "emerald", ref: paid, key: "active" };
    if (trial && trial > now) return { label: "Trial", tone: "amber", ref: trial, key: "trial" };
    return { label: "Expired", tone: "destructive", ref: paid || trial, key: "expired" };
  };

  const visibleCompanies = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return companies.filter((c) =>
      (typeFilter === "all" || (c.type || "business") === typeFilter) &&
      (statusFilter === "all" || companyStatus(c).key === statusFilter) &&
      (!ql || (c.name || "").toLowerCase().includes(ql) || (c.owner_email || "").toLowerCase().includes(ql))
    );
  }, [companies, typeFilter, statusFilter, q]);
  const companyWin = useWindowed(visibleCompanies);

  const schoolCount = useMemo(() => companies.filter((c) => (c.type || "business") === "school").length, [companies]);
  const careCount = useMemo(() => companies.filter((c) => c.type === "care").length, [companies]);
  const daycareCount = useMemo(() => companies.filter((c) => c.type === "daycare").length, [companies]);
  const businessCount = companies.length - schoolCount - careCount - daycareCount;
  const defaultPrice = Number(stats?.monthly_price_usd ?? 29);
  const annualPrice = Number(stats?.annual_price_usd ?? defaultPrice * 10);


  return (
    <main className="px-6 md:px-10 py-8" data-testid="platform-companies-page">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Companies</h1>
          <div className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
            {companies.length} registered · {schoolCount} school{schoolCount === 1 ? "" : "s"} · {businessCount} office{businessCount === 1 ? "" : "s"} · <span data-testid="platform-care-count">{careCount} care home{careCount === 1 ? "" : "s"}</span> · <span data-testid="platform-daycare-count">{daycareCount} daycare{daycareCount === 1 ? "" : "s"}</span> ·
            <span className="font-semibold text-slate-700" data-testid="platform-default-price">${defaultPrice.toFixed(0)}/mo · ${annualPrice.toFixed(0)}/yr each</span>
            <Link
              to="/platform/pricing"
              data-testid="platform-edit-default-price"
              title="Open the pricing page"
              className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
            >
              <Pencil className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <button
          onClick={load}
          data-testid="platform-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex flex-col gap-3 px-5 py-4 border-b border-slate-200">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search company or email…"
                data-testid="platform-company-search"
                className="w-full h-9 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="platform-type-filter">
              {TYPE_OPTS.map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setTypeFilter(opt.v)}
                  data-testid={`platform-filter-${opt.v}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    typeFilter === opt.v ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="platform-status-filter">
            {STATUS_OPTS.map((opt) => (
              <button
                key={opt.v}
                onClick={() => setStatusFilter(opt.v)}
                data-testid={`platform-status-filter-${opt.v}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  statusFilter === opt.v ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="platform-companies-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-3">Company</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Admin email</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Renews / Trial ends</th>
                <th className="px-5 py-3">Joined</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleCompanies.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-5 py-14 text-center text-slate-500">No companies match this filter.</td></tr>
              )}
              {companyWin.items.map((c) => {
                const s = companyStatus(c);
                const tm = typeMeta(c.type || "business");
                return (
                  <tr key={c.id} className={`border-t border-slate-100 hover:bg-slate-50 ${c.deactivated ? "opacity-60" : ""}`} data-testid={`platform-company-${c.id}`}>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => nav(`/platform/companies/${c.id}`)}
                        data-testid={`platform-company-link-${c.id}`}
                        className="text-left font-medium text-slate-900 hover:text-primary hover:underline underline-offset-2 transition"
                        title="View admins, counts & payment history"
                      >
                        {c.name}
                      </button>
                      <div className="text-xs text-slate-500 font-mono">{c.id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${tm.pill}`} data-testid={`platform-type-${c.id}`}>
                        <tm.Icon className="h-3 w-3" />
                        {c.type === "care" ? "Care home" : c.type === "school" ? "School" : c.type === "daycare" ? "Daycare" : "Office"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{c.owner_email}</td>
                    <td className="px-5 py-3">
                      <StatusPill tone={s.tone} label={s.label} />
                    </td>
                    <td className="px-5 py-3 text-slate-700 text-sm">
                      {s.ref ? (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(s.ref).toLocaleDateString()}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-slate-700 text-sm">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => setDeactFor(c)}
                          data-testid={`platform-deact-${c.id}`}
                          title={c.deactivated ? "Reactivate this account" : "Deactivate this account (blocks all logins)"}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                            c.deactivated
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                              : "border-red-200 bg-white text-red-600 hover:bg-red-50"
                          }`}
                        >
                          {c.deactivated ? <Power className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          onClick={() => { setTypeFor(c); setNewType(c.type || "business"); }}
                          data-testid={`platform-switch-type-${c.id}`}
                          title="Change building type"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition"
                        >
                          <tm.Icon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => { setExtendFor(c); setExtendDays("30"); }}
                          data-testid={`platform-extend-${c.id}`}
                          title="Grant or extend paid subscription time"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition"
                        >
                          <CalendarPlus className="h-3.5 w-3.5" /> Extend
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              <ShowMoreBar win={companyWin} colSpan={7} testid="companies-show-more" />
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={!!typeFor} onOpenChange={(o) => { if (!o) setTypeFor(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="switch-type-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              Change building type
            </DialogTitle>
            <DialogDescription>
              {typeFor && (
                <><strong>{typeFor.name}</strong> is currently a <strong>{typeMeta(typeFor.type || "business").label}</strong>.
                {" "}Changing the type changes which features (students & buses vs. visitors vs. clients & stays) the tenant sees. Only you, the platform owner, can do this.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2" data-testid="switch-type-options">
            {Object.entries(TYPE_META).map(([v, m]) => (
              <button key={v} type="button" onClick={() => setNewType(v)} data-testid={`switch-type-opt-${v}`}
                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition ${newType === v ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                <m.Icon className={`h-5 w-5 ${newType === v ? "text-primary" : "text-slate-500"}`} />
                <span className="text-sm font-medium text-slate-900">{m.label}</span>
              </button>
            ))}
          </div>
          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => setTypeFor(null)}
              data-testid="switch-type-cancel"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveType}
              disabled={savingType}
              data-testid="switch-type-confirm"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition disabled:opacity-60"
            >
              {savingType && <Loader2 className="h-4 w-4 animate-spin" />}
              Save type
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deactFor} onOpenChange={(o) => { if (!o) setDeactFor(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="deact-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {deactFor?.deactivated
                ? <Power className="h-5 w-5 text-emerald-600" />
                : <Ban className="h-5 w-5 text-red-600" />}
              {deactFor?.deactivated ? "Reactivate account" : "Deactivate account"}
            </DialogTitle>
            <DialogDescription>
              {deactFor?.deactivated ? (
                <>Restore <strong>{deactFor?.name}</strong>. Everyone in this company will be able to sign in again immediately.</>
              ) : (
                <>Deactivate <strong>{deactFor?.name}</strong>. Every login for this company — admins, teachers, officers, kiosk and parents — will be blocked until you reactivate it. No data is deleted.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => setDeactFor(null)}
              data-testid="deact-cancel"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveDeact}
              disabled={savingDeact}
              data-testid="deact-confirm"
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition disabled:opacity-60 ${
                deactFor?.deactivated ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {savingDeact && <Loader2 className="h-4 w-4 animate-spin" />}
              {deactFor?.deactivated ? "Reactivate" : "Deactivate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!extendFor} onOpenChange={(o) => { if (!o) setExtendFor(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-primary" /> Extend {extendFor?.name}
            </DialogTitle>
            <DialogDescription>
              Adds paid days on top of the current subscription (or starts from today if expired). Use it to comp time or resolve a payment issue.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveExtend} className="space-y-4 pt-1">
            <label className="block">
              <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Days to add</span>
              <input
                type="number"
                min="1"
                step="1"
                autoFocus
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                data-testid="extend-days-input"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
            <div className="flex gap-2 text-xs">
              {[30, 90, 365].map((d) => (
                <button key={d} type="button" onClick={() => setExtendDays(String(d))}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-600 hover:bg-slate-100 transition">
                  +{d}d
                </button>
              ))}
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={savingExtend}
                data-testid="extend-save"
                className="h-10 rounded-lg bg-primary px-5 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 hover:shadow disabled:opacity-60 transition"
              >
                {savingExtend ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Extend subscription
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </main>
  );
}

function StatusPill({ tone, label }) {
  const cls = {
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    destructive: "bg-red-100 text-red-800",
  }[tone] || "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${cls}`}>
      {label}
    </span>
  );
}
